import React, { useMemo } from "react";
import { isEqual, pick } from "lodash";
import { CircleX } from "@sonder/ui/source/icons";
import { CircleButton } from "@sonder/ui/source/styled/fragments/CircleButton";
import { useSegment } from "analytics/segment";
import { setFilters as setCheckoutsFilters } from "checkouts/actions";
import { initialState as initialCheckoutsState } from "checkouts/reducer";
import { useStore as useCheckoutsStore } from "checkouts/store";
import { setFilters as setScheduleFilters } from "schedule/actions";
import { initialState as initialScheduleState } from "schedule/reducer";
import { useStore as useScheduleStore } from "schedule/store";
import { bulkSetFilters } from "tasks/actions";
import { initialState as initialTaskState } from "tasks/reducer";
import { useStore as useTasksStore } from "tasks/store";

const sharedState = {
  buildingIds: [],
  checkoutStatuses: true,
  sameDayCleansOnly: true,
};

/**
 * NOTE: This ClearFilterButton is a little annoying as it has to juggle Checkouts, Task and Scheduler stores.
 * Specifically, `sharedState` contains the fields that are consumed by the ScheduleView from the Task store.
 * As such, we pick the fields that this ClearFilterButton actually cares about.
 */

export const ClearFilterButton = () => {
  const { track } = useSegment();

  const {
    dispatch: dispatchCheckouts,
    state: { filters: globalCheckoutsFilters },
  } = useCheckoutsStore();
  const {
    dispatch: dispatchSchedule,
    state: { filters: globalScheduleFilters },
  } = useScheduleStore();
  const {
    dispatch: dispatchTasks,
    state: { filters: globalTaskFilters },
  } = useTasksStore();

  const sharedFilterKeys = Object.keys(sharedState);
  const relevantGlobalCheckoutsStateFilters = pick(
    globalCheckoutsFilters,
    sharedFilterKeys,
  );
  const relevantInitialCheckoutsStateFilters = pick(
    initialCheckoutsState.filters,
    sharedFilterKeys,
  );
  const relevantGlobalTaskStateFilters = pick(
    globalTaskFilters,
    sharedFilterKeys,
  );
  const relevantInitialTaskStateFilters = pick(
    initialTaskState.filters,
    sharedFilterKeys,
  );
  const relevantGlobalScheduleFilters = pick(
    globalScheduleFilters,
    sharedFilterKeys,
  );
  const relevantInitialScheduleStateFilters = pick(
    initialScheduleState.filters,
    sharedFilterKeys,
  );
  const isInitialState = useMemo(
    () =>
      isEqual(
        relevantGlobalCheckoutsStateFilters,
        relevantInitialCheckoutsStateFilters,
      ) &&
      isEqual(
        relevantGlobalTaskStateFilters,
        relevantInitialTaskStateFilters,
      ) &&
      isEqual(
        relevantGlobalScheduleFilters,
        relevantInitialScheduleStateFilters,
      ),
    [relevantGlobalTaskStateFilters],
  );
  return !isInitialState ? (
    <CircleButton
      backgroundVariant="light"
      title="Clear Filters"
      onClick={() => {
        dispatchCheckouts(setCheckoutsFilters(initialCheckoutsState.filters));
        dispatchTasks(
          bulkSetFilters({
            ...initialTaskState.filters,
            city: globalTaskFilters.city,
          }),
        );
        dispatchSchedule(setScheduleFilters(initialScheduleState.filters));
        track({
          eventName: "checkouts_clear_saved_filter",
          action: "click",
          element: "checkouts_clear_saved_filter.select_filter",
        });
      }}
    >
      <CircleX />
    </CircleButton>
  ) : null;
};
